import Vue, { computed, ref, watch } from 'vue'
import VueI18n from '@/i18n'
import GameService from '@/services/game.service'
import store from '@/store'
import { getPublicChannel, unsubscribePublicChannel } from '@/ably'
import router from '@/router'
import { routeAfterGame } from '@/use/useStartPage'
import Swal from 'sweetalert2'
import { uuidv4 } from '@/lib/uuid'
import { GameToastMessage } from '@/lib/toast'
import { useStopwatch } from '@/use/useStopwatch'
import { useRouteQuery } from '@/use/useRouteQuery'

const keyboardShortcuts = ref(true)
const timer = ref(null)
const id = ref(null)
const positionAssignments = ref({
  homeTeam: {},
  awayTeam: {}
})
const draftPositionAssignments = ref({
  homeTeam: {},
  awayTeam: {}
})
const clickedButton = ref('')
const selectedPlayerHome = ref('')
const selectedPlayerAway = ref('')
const log = ref({
  on: '',
  teammate: '',
  against: ''
})

const playerPopupBodyMaxHeight = ref(false)
const playerActionsScroll = ref(false)
const countdownLoaded = ref(false)

const timerTime = ref(null)
const teamOn = ref('')
const stage = ref('on')
const type = ref('')
const relatedTo = ref('')
const teamSelect = ref(null)
const playerSelect = ref('')
const playerSelection = ref(false)
const isTouchHold = ref(false)
const isTimeEditing = ref(false)
const ablyChannel = ref(null)
const showMobileTeamActionsPopup = ref(false)
const showMobilePlayerActionsPopup = ref(false)
const showSubstitutionModal = ref(false)
const substitutionLoading = ref(false)
const substitutionMode = ref(localStorage.getItem('substitutionMode') || 'classic')
const selectedSubstitutions = ref([])
const previousSubstitutions = ref([])
const substitutions = ref([])
const gameLogs = ref([])
const focusedPlayerIndex = ref(0)
const focusedTeam = ref('home')
const playerContextMenu = ref(null)
const teamContextMenu = ref(null)
const substitutionTimeInputs = ref(['', '', '', ''])
const substitutionInputErrors = ref([false, false, false, false])
const substitutionTime = ref(0)
const editingSubstitutionUuid = ref(null)
const editingSubstitutionPeriodUuid = ref(null)
const logQueue = ref([])
const isProcessingQueue = ref(false)
const showOptionsModal = ref(false)
const optionsModalStack = ref([])

export function useLive () {
  const isSidebarOpen = useRouteQuery('sidebar', false)
  const sidebarTab = useRouteQuery('history', 'logs', 'single', {
    deleteIf: (val) => !['logs', 'substitutions'].includes(val)
  })

  const {
    diffTime,
    isKeyboardShortcutsEnabled,
    currentTimeToShow,
    isActiveCurrentStopwatch,
    activeStopwatch,
    stopwatchId,
    numberOfRegularPeriods,
    prepareTimeInputs
  } = useStopwatch()

  const isMobile = computed(() => false)
  const model = computed(() => store.state.main.model)
  const config = computed(() => store.state.config)

  // Get period time info directly from model/config for substitution time validation
  const substitutionIsReverse = computed(() => config.value.time_reverse)
  const substitutionCurrentPeriod = computed(() => {
    // Find current game period from gamePeriods array
    if (!model.value.gamePeriods || !model.value.currentGamePeriodUuid) return null
    return model.value.gamePeriods.find(p => p.uuid === model.value.currentGamePeriodUuid)
  })
  const substitutionPeriodTime = computed(() => model.value.schedule?.periodTime)
  const substitutionOvertimePeriodTime = computed(() => model.value.schedule?.overtimePeriodTime)

  const periodMaxTimeOnPeriod = computed(() => {
    if (substitutionIsReverse.value) {
      const periodType = substitutionCurrentPeriod.value?.periodType ?? 'REGULAR'
      if (periodType === 'OVERTIME') {
        return substitutionOvertimePeriodTime.value
      }
      return substitutionPeriodTime.value
    }

    const periodNumber = substitutionCurrentPeriod.value?.periodNumber
    const periodType = substitutionCurrentPeriod.value?.periodType ?? 'REGULAR'

    if (periodType === 'OVERTIME') {
      return (numberOfRegularPeriods.value * substitutionPeriodTime.value) + ((periodNumber || 1) * substitutionOvertimePeriodTime.value)
    }

    return substitutionPeriodTime.value * periodNumber
  })

  const periodMinTimeOnPeriod = computed(() => {
    if (substitutionIsReverse.value) {
      return 0
    }

    const periodNumber = substitutionCurrentPeriod.value?.periodNumber
    const periodType = substitutionCurrentPeriod.value?.periodType ?? 'REGULAR'

    if (periodType === 'OVERTIME') {
      return (numberOfRegularPeriods.value * substitutionPeriodTime.value) + ((periodNumber - 1) * substitutionOvertimePeriodTime.value)
    }

    return substitutionPeriodTime.value * (periodNumber - 1)
  })
  const lineupPositions = computed(() => (config.value.live.positions ?? []).filter(a => a.count > 0))
  const notifications = computed(() => config.value.live.notifications)
  const maxTeamPlayersInGame = computed(() => config.value.in_game_max_players ?? 5)
  const homeTeamOrderClass = computed(() => {
    if (reversedTeams.value) {
      if (store.state.main.actionsPosition === '3') {
        return 'order-2'
      } else {
        return 'order-3'
      }
    } else {
      if (store.state.main.actionsPosition === '1') {
        return 'order-2'
      } else {
        return 'order-1'
      }
    }
  })

  const actionsOrderClass = computed(() => `order-${store.state.main.actionsPosition}`)
  const awayTeamOrderClass = computed(() => {
    if (store.state.main.reversedTeams) {
      if (store.state.main.actionsPosition === '1') {
        return 'order-2'
      } else {
        return 'order-1'
      }
    } else {
      if (store.state.main.actionsPosition === '3') {
        return 'order-2'
      } else {
        return 'order-3'
      }
    }
  })

  const clientId = computed(() => store.state.id)

  const actionsPosition = computed(() => store.state.main.actionsPosition)
  const reversedTeams = computed(() => store.state.main.reversedTeams)
  const invertedTable = computed(() => store.state.main.invertedTable)
  const autoUnselect = computed(() => store.state.main.autoUnselect)

  const score = computed(() => {
    if (reversedTeams.value) {
      return `${model.value.score.away} : ${model.value.score.home}`
    }

    return `${model.value.score.home} : ${model.value.score.away}`
  })

  const currentPeriod = computed(() => {
    return model.value.gamePeriods?.find(p => p.uuid === model.value.currentGamePeriodUuid)
  })

  const currentPeriodType = computed(() => {
    return currentPeriod.value ? currentPeriod.value.periodType : 'REGULAR'
  })

  const roundScore = computed(() => {
    if (!config.value.round_stat || !model.value.currentPeriodRoundScore) return null
    const rs = model.value.currentPeriodRoundScore
    return reversedTeams.value ? `${rs.away} : ${rs.home}` : `${rs.home} : ${rs.away}`
  })

  const scoreByPeriod = computed(() => {
    let score = ''
    model.value.periodScores?.forEach((a, b) => {
      const separator = b === 0 ? '' : ' | '
      if (reversedTeams.value) {
        score = score.concat(separator, `${a.homeScore} : ${a.awayScore}`)
      } else {
        score = score.concat(separator, `${a.homeScore} : ${a.awayScore}`)
      }
    })

    return score
  })

  const playerPreview = computed(() => {
    let player = null
    if (playerSelect.value && teamSelect.value === 'home') {
      player = model.value.homeTeam.players.find((player) => player.number_of_jersey === playerSelect.value)
    }
    if (playerSelect.value && teamSelect.value === 'away') {
      player = model.value.awayTeam.players.find((player) => player.number_of_jersey === playerSelect.value)
    }

    return player ? player.name : '#'
  })

  const buttonPlayerGroups = computed(() => config.value.logs.player)
  const buttonTeamGroups = computed(() => config.value.logs.team)
  const teamActions = computed(() => {
    const actions = {}
    Object.keys(config.value.logs.team).forEach((stat) => {
      actions[stat] = config.value.logs.team[stat]
    })
    return actions
  })

  const teamHotKeys = computed(() => {
    const hotKeys = []
    Object.keys(teamActions.value).forEach((action) => {
      if (teamActions.value[action].hotKey) {
        hotKeys.push({
          code: teamActions.value[action].hotKey.code,
          type: action,
          shiftKey: Boolean(playerActions.value[action].hotKey.shiftKey)
        })
      }
    })

    return hotKeys
  })

  const playerActions = computed(() => {
    const actions = {}
    const arrayGroups = Object.keys(config.value.logs.player)
    arrayGroups.forEach((group) => {
      Object.keys(config.value.logs.player[group]).forEach((stat) => {
        const action = config.value.logs.player[group][stat]
        if (action.options) {
          Object.keys(action.options).forEach((optKey) => {
            actions[optKey] = action.options[optKey]
          })
        } else {
          actions[stat] = action
        }
      })
    })
    return actions
  })

  const playerActionsAll = computed(() => {
    const actions = {}
    const arrayGroups = Object.keys(config.value.logs.player)
    arrayGroups.forEach((group) => {
      Object.keys(config.value.logs.player[group]).forEach((stat) => {
        const action = config.value.logs.player[group][stat]
        actions[stat] = action
        if (action.options) {
          Object.keys(action.options).forEach((optKey) => {
            actions[optKey] = action.options[optKey]
          })
        }
      })
    })
    return actions
  })

  const playerHotKeys = computed(() => {
    const hotKeys = []
    Object.keys(playerActions.value).forEach((action) => {
      if (playerActions.value[action].hotKey) {
        hotKeys.push({
          code: playerActions.value[action].hotKey.code,
          type: action,
          shiftKey: Boolean(playerActions.value[action].hotKey.shiftKey)
        })
      }
    })

    return hotKeys
  })

  const buttonPlayerTypes = computed(() => Object.keys(config.value.logs.player))
  const buttonTeamTypes = computed(() => Object.keys(config.value.logs.team))
  const doneDisable = computed(() => !(log.value.on !== '' && type.value !== ''))
  const disableAway = computed(() => {
    if (relatedTo.value !== 'player' && relatedTo.value !== '') {
      return true
    }
    if (teamOn.value === 'away') {
      if (stage.value === 'against') {
        return true
      }
    }
    if (teamOn.value === 'home') {
      if (stage.value === 'teammate') {
        return true
      }
    }
    return false
  })

  const disableHome = computed(() => {
    if (relatedTo.value !== 'player' && relatedTo.value !== '') {
      return true
    }
    if (teamOn.value === 'home') {
      if (stage.value === 'against') {
        return true
      }
    }
    if (teamOn.value === 'away') {
      if (stage.value === 'teammate') {
        return true
      }
    }
    return false
  })

  const teamUuid = computed(() => {
    if (teamOn.value === 'home') {
      return model.value.homeTeam.uuid
    } else if (teamOn.value === 'away') {
      return model.value.awayTeam.uuid
    }

    return null
  })

  const inGamePositionOrder = (player) => {
    if (!player.in_game_position) return Infinity
    const idx = lineupPositions.value.findIndex(p => p.id === player.in_game_position)
    return idx === -1 ? Infinity : idx
  }

  const homePlayersSorted = computed(() => {
    return [...(model.value.homeTeam.players ?? [])].sort((a, b) => {
      if (a.isInGame !== b.isInGame) return a.isInGame ? -1 : 1
      if (a.isInGame) {
        const posA = inGamePositionOrder(a)
        const posB = inGamePositionOrder(b)
        if (posA !== posB) return posA - posB
      }
      return a.number_of_jersey - b.number_of_jersey
    })
  })

  const homePlayingCount = computed(() => {
    return model.value.homeTeam.players?.filter(p => p.isInGame).length ?? 0
  })

  const awayPlayersSorted = computed(() => {
    return [...(model.value.awayTeam.players ?? [])].sort((a, b) => {
      if (a.isInGame !== b.isInGame) return a.isInGame ? -1 : 1
      if (a.isInGame) {
        const posA = inGamePositionOrder(a)
        const posB = inGamePositionOrder(b)
        if (posA !== posB) return posA - posB
      }
      return a.number_of_jersey - b.number_of_jersey
    })
  })

  const awayPlayingCount = computed(() => {
    return model.value.awayTeam.players?.filter(p => p.isInGame).length ?? 0
  })

  function getWillBeInGameUuids (teamPlayers) {
    if (substitutionMode.value === 'on-court') {
      return teamPlayers
        .filter(p => selectedSubstitutions.value?.includes(p.uuid))
        .map(p => p.uuid)
    }
    return teamPlayers
      .map(p => {
        const selected = selectedSubstitutions.value?.includes(p.uuid)
        return (selected ? !p.isInGame : p.isInGame) ? p.uuid : null
      })
      .filter(Boolean)
  }

  const getEffectivePos = (teamDraftKey, uuid, players) => {
    const draft = draftPositionAssignments.value[teamDraftKey]
    if (draft && uuid in draft) return draft[uuid]
    const player = players.find(p => p.uuid === uuid)
    return (player?.isInGame ? player.in_game_position : null) ?? null
  }

  // Names of the lineup positions (e.g. GK) that nobody holds, so the actions that go to that position (e.g. the
  // saves of a shot on target) are not recorded. Empty while nobody of the team is in the game.
  function getMissingPositions (inGamePositions) {
    if (!inGamePositions.length) return []
    return lineupPositions.value
      .filter(pos => !inGamePositions.includes(pos.id))
      .map(pos => pos.name)
  }

  const missingPositions = computed(() => {
    const currentPositions = (players) => (players ?? []).filter(p => p.isInGame).map(p => p.in_game_position)

    return {
      home: getMissingPositions(currentPositions(model.value.homeTeam?.players)),
      away: getMissingPositions(currentPositions(model.value.awayTeam?.players))
    }
  })

  // The same for the lineup that the open substitution dialog would save
  const draftMissingPositions = computed(() => {
    const missingOf = (players, draftKey) => {
      const willBeInGame = getWillBeInGameUuids(players ?? [])
      return getMissingPositions(willBeInGame.map(uuid => getEffectivePos(draftKey, uuid, players ?? [])))
    }

    return {
      home: missingOf(model.value.homeTeam?.players, 'homeTeam'),
      away: missingOf(model.value.awayTeam?.players, 'awayTeam')
    }
  })

  const positionRequirementsValid = computed(() => {
    if (!lineupPositions.value.length) return true

    const homePlayers = model.value.homeTeam?.players || []
    const awayPlayers = model.value.awayTeam?.players || []

    const homeInGame = getWillBeInGameUuids(homePlayers)
    const awayInGame = getWillBeInGameUuids(awayPlayers)

    const homeHasAnyPosition = homeInGame.some(uuid => getEffectivePos('homeTeam', uuid, homePlayers) !== null)
    const awayHasAnyPosition = awayInGame.some(uuid => getEffectivePos('awayTeam', uuid, awayPlayers) !== null)

    for (const pos of lineupPositions.value) {
      if (!pos.count) continue

      if (homeInGame.length > 0 && homeHasAnyPosition) {
        const count = homeInGame.filter(uuid => getEffectivePos('homeTeam', uuid, homePlayers) === pos.id).length
        if (count !== pos.count) return false
      }

      if (awayInGame.length > 0 && awayHasAnyPosition) {
        const count = awayInGame.filter(uuid => getEffectivePos('awayTeam', uuid, awayPlayers) === pos.id).length
        if (count !== pos.count) return false
      }
    }

    return true
  })

  const isSubstitutionValid = computed(() => {
    if (substitutionMode.value === 'on-court') {
      const homePlayers = model.value.homeTeam.players || []
      const awayPlayers = model.value.awayTeam.players || []

      const homeSelectedCount = homePlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid)).length
      const awaySelectedCount = awayPlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid)).length

      if (homeSelectedCount > maxTeamPlayersInGame.value || awaySelectedCount > maxTeamPlayersInGame.value) return false
      return positionRequirementsValid.value
    }

    if (selectedSubstitutions.value?.length === 0 && !lineupPositions.value.length) {
      return false
    }

    const homePlayers = model.value.homeTeam.players || []
    const awayPlayers = model.value.awayTeam.players || []

    const validate = (playersOnTeam) => {
      const teamSelected = playersOnTeam.filter(p => selectedSubstitutions.value?.includes(p.uuid))
      if (teamSelected.length === 0) {
        return playersOnTeam.filter(p => p.isInGame).length <= maxTeamPlayersInGame.value
      }

      const currentInGameCount = playersOnTeam.filter(p => p.isInGame).length
      const inGameSelectedCount = teamSelected.filter(p => p.isInGame).length
      const outGameSelectedCount = teamSelected.filter(p => !p.isInGame).length

      const newInGameCount = currentInGameCount - inGameSelectedCount + outGameSelectedCount
      return newInGameCount <= maxTeamPlayersInGame.value
    }

    if (!validate(homePlayers)) return false
    if (!validate(awayPlayers)) return false

    return positionRequirementsValid.value
  })

  const substitutionDiff = computed(() => {
    const homePlayers = model.value.homeTeam.players
    const awayPlayers = model.value.awayTeam.players

    const getDiff = (playersOnTeam) => {
      const teamSelected = (playersOnTeam || []).filter(p => selectedSubstitutions.value?.includes(p.uuid))
      const current = (playersOnTeam || []).filter(p => p.isInGame).length

      if (substitutionMode.value === 'on-court') {
        return { current, next: teamSelected.length }
      }

      if (teamSelected.length === 0) return { current, next: current }

      const inGameSelected = teamSelected.filter(p => p.isInGame).length
      const outGameSelected = teamSelected.filter(p => !p.isInGame).length
      const next = current - inGameSelected + outGameSelected
      return { current, next }
    }

    return {
      home: getDiff(homePlayers),
      away: getDiff(awayPlayers)
    }
  })

  const homeLabel = computed(() => {
    if (stage.value === 'on') {
      if (teamOn.value !== '') {
        return {
          text: 'select_action',
          bg: 'info'
        }
      } else {
        return {
          text: 'select_player',
          bg: 'success'
        }
      }
    }
    if (teamOn.value === 'home') {
      if (stage.value === 'teammate') {
        return {
          text: getStateStatType(),
          bg: 'info'
        }
      } else {
        return {
          text: 'disabled',
          bg: 'disabled'
        }
      }
    } else {
      if (stage.value === 'against') {
        return {
          text: getStateStatType(),
          bg: 'warning'
        }
      } else {
        return {
          text: 'disabled',
          bg: 'disabled'
        }
      }
    }
  })

  const awayLabel = computed(() => {
    if (stage.value === 'on') {
      if (teamOn.value !== '') {
        return {
          text: 'select_action',
          bg: 'info'
        }
      } else {
        return {
          text: 'select_player',
          bg: 'success'
        }
      }
    }
    if (teamOn.value === 'away') {
      if (stage.value === 'teammate') {
        return {
          text: getStateStatType(),
          bg: 'info'
        }
      } else {
        return {
          text: 'disabled',
          bg: 'disabled'
        }
      }
    } else {
      if (stage.value === 'against') {
        return {
          text: getStateStatType(),
          bg: 'warning'
        }
      } else {
        return {
          text: 'disabled',
          bg: 'disabled'
        }
      }
    }
  })

  function translateStat (stat, type) {
    return VueI18n.t(`sports.${config.value.sport}.stats.${stat}.${type}`)
  }

  function initPositionAssignments () {
    const newAssignments = { homeTeam: {}, awayTeam: {} }

    const extractFromPlayers = (players, teamKey) => {
      players.forEach(player => {
        if (player.in_game_position && player.isInGame) {
          newAssignments[teamKey][player.uuid] = player.in_game_position
        }
      })
    }

    extractFromPlayers(model.value.homeTeam.players ?? [], 'homeTeam')
    extractFromPlayers(model.value.awayTeam.players ?? [], 'awayTeam')

    positionAssignments.value = newAssignments
  }

  function initDraftPositions () {
    draftPositionAssignments.value = {
      homeTeam: { ...positionAssignments.value.homeTeam },
      awayTeam: { ...positionAssignments.value.awayTeam }
    }
  }

  function getDraftPosition (teamKey, playerUuid) {
    const assignments = draftPositionAssignments.value[teamKey + 'Team'] ?? {}
    return playerUuid in assignments ? assignments[playerUuid] : undefined
  }

  function toggleDraftPosition (teamKey, playerUuid, positionId) {
    const teamKeyFull = teamKey + 'Team'
    const assignments = { ...draftPositionAssignments.value[teamKeyFull] }

    if (assignments[playerUuid] === positionId) {
      assignments[playerUuid] = null
    } else {
      const pos = lineupPositions.value.find(p => p.id === positionId)
      const count = pos?.count ?? 1

      const teamPlayers = teamKey === 'home'
        ? (model.value.homeTeam.players ?? [])
        : (model.value.awayTeam.players ?? [])

      const willBeInGameUuids = getWillBeInGameUuids(teamPlayers)

      const holders = teamPlayers
        .filter(p => willBeInGameUuids.includes(p.uuid) && p.uuid !== playerUuid)
        .filter(p => {
          const effectivePos = p.uuid in assignments ? assignments[p.uuid] : (p.isInGame ? p.in_game_position : null)
          return effectivePos === positionId
        })
        .sort((a, b) => a.number_of_jersey - b.number_of_jersey)

      if (holders.length >= count) {
        assignments[holders[holders.length - 1].uuid] = null
      }

      assignments[playerUuid] = positionId
    }

    draftPositionAssignments.value[teamKeyFull] = assignments
  }

  // The lineup position (e.g. GK) of an in-game player. The saves and other "auto actions" of a position are created
  // by le-api (LiveController::processAutoActions); here the position is only shown next to the player.
  function inGamePosition (player) {
    if (!player.isInGame) return undefined
    return (config.value.live.positions ?? []).find(position => position.id === player.in_game_position)
  }
  function playerPositionLabel (player) {
    return inGamePosition(player)?.id ?? null
  }
  function playerPositionColor (player) {
    return inGamePosition(player)?.color ?? ''
  }

  function gameLogColor (gameLog) {
    if (gameLog.relatedTo === 'player') {
      return playerActions.value[gameLog.type]?.bgColor ?? '#70b9eb'
    } else {
      return '#70b9eb'
    }
  }
  function translateLog (gameLog) {
    if (gameLog.against === null && gameLog.teammate === null) {
      return VueI18n.t('live.sports.' + config.value.sport + '.logs.' + gameLog.relatedTo + '.' + gameLog.type + '.alone', [gameLog.on])
    } else {
      if (gameLog.against !== null) {
        return VueI18n.t('live.sports.' + config.value.sport + '.logs.' + gameLog.relatedTo + '.' + gameLog.type + '.against', [gameLog.on, gameLog.against])
      } else if (gameLog.teammate !== null) {
        return VueI18n.t('live.sports.' + config.value.sport + '.logs.' + gameLog.relatedTo + '.' + gameLog.type + '.teammate', [gameLog.on, gameLog.teammate])
      }
    }
  }
  function eventCodeMapping (code) {
    return code.replace(/^Numpad([1-9])$/, 'Digit$1')
  }
  const keyDownListener = (event) => {
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return
    }

    const eventCode = eventCodeMapping(event.code)

    if (showOptionsModal.value) {
      const currentOptions = optionsModalStack.value[optionsModalStack.value.length - 1]?.options
      if (currentOptions) {
        const optionKeys = Object.keys(currentOptions)
        const digitMatch = eventCode.match(/^Digit(\d)$/)
        if (digitMatch) {
          const idx = parseInt(digitMatch[1]) - 1
          if (idx >= 0 && idx < optionKeys.length) {
            event.preventDefault()
            selectOption(optionKeys[idx], currentOptions[optionKeys[idx]])
          }
        }
      }
      return
    }

    if (!keyboardShortcuts.value) return

    if (eventCode === 'KeyX') {
      event.preventDefault()
      toggleSidebar('logs')
    }
    if (eventCode === 'KeyZ') {
      event.preventDefault()
      if (model.value.hasSubstitutions) {
        toggleSidebar('substitutions')
      }
    }
    if (eventCode === 'Comma') {
      event.preventDefault()
      if (model.value.hasSubstitutions) {
        openSubstitutionModal()
      }
    }

    if (relatedTo.value === 'player') {
      const playerHotKey = playerHotKeys.value.find((key) => {
        if (event.shiftKey === true) {
          return (key.code === eventCode) && key.shiftKey
        } else {
          return key.code === eventCode && !key.shiftKey
        }
      })

      if (playerHotKey && log.value.on) {
        event.preventDefault()

        selectType(playerHotKey.type)
        clickTypeButton(playerHotKey.type)
      }
    } else if (relatedTo.value === 'team') {
      const teamHotKey = teamHotKeys.value.find((key) => {
        if (event.shiftKey === true) {
          return (key.code === eventCode) && key.shiftKey
        } else {
          return key.code === eventCode && !key.shiftKey
        }
      })

      if (teamHotKey && log.value.on && relatedTo.value === 'team') {
        event.preventDefault()

        selectType(teamHotKey.type)
        clickTypeButton(teamHotKey.type)
      }
    }
  }
  function clickTypeButton (type) {
    clickedButton.value = type
    setTimeout(() => {
      clickedButton.value = ''
    }, 100)
  }

  function showPlayerActionsPopup () {
    setTimeout(() => {
      triggerPlayerActionsPopup()
    }, 100)
  }
  function triggerPlayerActionsPopup () {
    showMobilePlayerActionsPopup.value = true

    if (isMobile.value) {
      setTimeout(() => {
        const playerStats = document.getElementById('playerStats')
        const playerPopupBody = document.getElementById('playerPopupBody')

        const playerPopupTitle = document.getElementById('playerPopupTitle')
        const playerPopupClose = document.getElementById('playerPopupClose')
        const playerPopup = document.getElementById('playerPopup')

        const playerStatsHeight = playerStats.clientHeight
        const playerPopupBodyHeight = playerPopupBody.clientHeight

        const playerPopupHeight = playerPopup.clientHeight
        const playerPopupTitleHeight = playerPopupTitle.clientHeight
        const playerPopupCloseHeight = playerPopupClose.clientHeight

        playerActionsScroll.value = playerPopupBodyHeight - playerStatsHeight

        playerPopupBodyMaxHeight.value = playerPopupHeight - playerPopupTitleHeight - playerPopupCloseHeight - 16
      })
    }
  }

  function selectEntity (related, team, uuid) {
    if (!uuid) {
      resetLog()
      return
    }
    relatedTo.value = related
    if (relatedTo.value === 'game') {
      resetLog()
      // TODO: add game logs
    } else if (relatedTo.value === 'team') {
      if (log.value.on === uuid) {
        resetLog()
      } else {
        resetLog(related)
        teamOn.value = team
        log.value.on = uuid
        showMobileTeamActionsPopup.value = true
      }
    } else if (relatedTo.value === 'player') {
      if ((disableAway.value && team === 'away') || (disableHome.value && team === 'home')) {
        if (teamOn.value === team && stage.value === 'against') {
          resetLog('player')
          log.value.on = uuid
          type.value = ''
          teamOn.value = team
        }

        if (teamOn.value !== team && stage.value === 'teammate') {
          resetLog('player')
          log.value.on = uuid
          type.value = ''
          teamOn.value = team
        }
      } else {
        if (log.value.on === uuid) {
          resetLog()
        } else {
          if (log.value.teammate === uuid) {
            log.value.teammate = ''
            stage.value = 'teammate'
          } else if (log.value.against === uuid) {
            log.value.against = ''
            stage.value = 'against'
          } else {
            log.value[stage.value] = uuid
            if (stage.value === 'on') {
              type.value = ''
              teamOn.value = team
              showPlayerActionsPopup()
            } else {
              acceptLog()
            }
          }
        }
      }
    }
  }
  function selectType (button) {
    log.value.against = ''
    log.value.teammate = ''

    const action = relatedTo.value === 'team'
      ? teamActions.value[button]
      : playerActionsAll.value[button]

    if (action?.options) {
      optionsModalStack.value = [{ key: button, options: action.options }]
      showOptionsModal.value = true
      showMobileTeamActionsPopup.value = false
      showMobilePlayerActionsPopup.value = false
      return
    }

    type.value = button
    if (relatedTo.value === 'team') {
      stage.value = 'on'
      acceptLog()
    }

    if (relatedTo.value === 'player') {
      if (playerActions.value[type.value].against) {
        stage.value = 'against'
      } else if (playerActions.value[type.value].teammate) {
        stage.value = 'teammate'
      } else {
        acceptLog()
      }
    }

    showMobileTeamActionsPopup.value = false
    showMobilePlayerActionsPopup.value = false
  }

  function selectOption (optionKey, option) {
    if (option.options) {
      optionsModalStack.value.push({ key: optionKey, options: option.options })
      return
    }
    showOptionsModal.value = false
    optionsModalStack.value = []
    selectType(optionKey)
  }

  function closeOptionsModal () {
    showOptionsModal.value = false
    optionsModalStack.value = []
  }

  function optionsModalBack () {
    if (optionsModalStack.value.length > 1) {
      optionsModalStack.value.pop()
    } else {
      closeOptionsModal()
    }
  }
  async function acceptLog () {
    const data = { ...log.value }
    data.time = config.value.scorekeeper_stopwatch_disabled ? null : diffTime.value
    data.type = type.value
    data.gamePeriodUuid = model.value.currentGamePeriodUuid
    data.team = teamUuid.value
    data.relatedTo = relatedTo.value

    type.value = ''
    stage.value = 'on'
    log.value.against = ''
    log.value.teammate = ''

    if (autoUnselect.value || isMobile.value) {
      resetLog()
    }

    await storeLog(data)
  }
  async function processQueue () {
    if (isProcessingQueue.value || logQueue.value.length === 0) {
      return
    }

    isProcessingQueue.value = true
    const data = logQueue.value[0]

    try {
      await GameService.gameLog(data).catch((error) => {
        // error.response is missing when the request got no answer (lost connection, timeout)
        if (error.response?.data === 'game.not-live') {
          GameService.status().then((response) => {
            if (response.data === 'FINISHED') {
              router.push({ name: 'archive' })
            } else {
              router.push(routeAfterGame(store))
            }
          })

          return
        }

        // The event is dropped from the queue below, so the scorekeeper has to know it was not saved
        GameToastMessage(VueI18n.t('live.log_not_saved'), 'error')
      })
    } finally {
      logQueue.value.shift()
      isProcessingQueue.value = false
      processQueue()
    }
  }
  function storeLog (data) {
    logQueue.value.push(data)
    processQueue()
  }
  function gameUpdate (game, data) {
    gameLogs.value.unshift(data.log)

    Vue.notify({
      group: 'logs',
      data: data.log,
      duration: 5000
    })

    model.value.score.home = game.home_team_score_total
    model.value.score.away = game.away_team_score_total
    model.value.periodScores = data.periodScores
    if (data.currentPeriodRoundScore !== undefined) {
      model.value.currentPeriodRoundScore = data.currentPeriodRoundScore
    }
    model.value.homeTeam.players = data.homePlayers
    model.value.awayTeam.players = data.awayPlayers
    model.value.homeTeam.period_stats = data.home_team_period_stats
    model.value.homeTeam.by_period_stats = data.home_team_by_period_stats
    model.value.awayTeam.period_stats = data.away_team_period_stats
    model.value.awayTeam.by_period_stats = data.away_team_by_period_stats

    initPositionAssignments()

    if (notifications.value) {
      checkForNotifications(data.log)
    }
  }
  async function checkForNotifications (log) {
    const team = model.value.homeTeam.uuid === log.team ? model.value.homeTeam : model.value.awayTeam
    const teamAgainst = model.value.homeTeam.uuid === log.team ? model.value.awayTeam : model.value.homeTeam

    if (notifications?.value.player) {
      if (log.playerOn) {
        await checkPlayerNotifications(team, log.playerOn)
      }
      if (log.playerTeammate) {
        await checkPlayerNotifications(team, log.playerTeammate)
      }
      if (log.playerAgainst) {
        await checkPlayerNotifications(teamAgainst, log.playerAgainst)
      }
    }

    if (notifications?.value.team) {
      checkTeamNotifications(team, log)
    }
  }
  async function checkTeamNotifications (team, log) {
    const lastPeriodIndex = numberOfRegularPeriods.value - 1
    const currentPeriodStatIndex = currentPeriodType.value === 'OVERTIME'
      ? numberOfRegularPeriods.value + currentPeriod.value?.periodNumber - 1
      : currentPeriod.value?.periodNumber - 1

    for (const rule of notifications.value.team.period) {
      let actionChangedStats = null
      if (log.relatedTo === 'player') {
        actionChangedStats = Object.keys(playerActions.value[log.type].on)
      } else if (log.relatedTo === 'team') {
        actionChangedStats = Object.keys(teamActions.value[log.type].on)
      }

      const intersection = rule.fields.filter(x => actionChangedStats.includes(x))

      if (!intersection.length) continue

      const teamStats = { ...team.by_period_stats[currentPeriodStatIndex] }

      if (currentPeriodType.value === 'OVERTIME') {
        const teamStatsLastPeriod = { ...team.by_period_stats[lastPeriodIndex] }
        Object.keys(teamStats).forEach((statKey) => {
          teamStats[statKey] = teamStats[statKey] + teamStatsLastPeriod[statKey]
        })
      }

      let sum = 0
      const fields = []
      rule.fields.forEach((field) => {
        sum += teamStats[field]
        fields.push({ name: field, value: teamStats[field] })
      })

      if (sum === rule.value) {
        let content = ''
        fields.forEach((f) => {
          content += `<li><strong>${translateStat(f.name, 'original')}: <span class="text-warning">${f.value}</span></strong></li>`
        })

        await Swal.fire({
          title: VueI18n.t(`live.notifications.team.period.${rule.name}`, { team: `${team.name}` }),
          icon: 'warning',
          html: `<ul>${content}</ul>`,
          showCloseButton: true,
          showCancelButton: false,
          confirmButtonText:
            '<i class="fa fa-thumbs-up"></i>',
          confirmButtonAriaLabel: 'Ok!'
        })
      }
    }
  }
  async function checkPlayerNotifications (team, player) {
    const playerStat = team.players.find((p) => p.slug === player.slug)

    // total notifications
    for (const rule of notifications.value.player.total) {
      let sum = 0
      const fields = []
      rule.fields.forEach((field) => {
        sum += playerStat[field]
        fields.push({ name: field, value: playerStat[field] })
      })

      if (sum === rule.value) {
        let content = ''

        fields.filter((f) => f.value > 0).forEach((f) => {
          content += `<li><strong>${translateStat(f.name, 'original')}: <span class="text-warning">${f.value}</span></strong></li>`
        })

        await Swal.fire({
          title: VueI18n.t(`live.notifications.player.total.${rule.name}`, { player: `#${playerStat.number_of_jersey} - ${player.name}` }),
          icon: 'warning',
          html: `<ul>${content}</ul>`,
          showCloseButton: true,
          showCancelButton: false,
          confirmButtonText:
            '<i class="fa fa-thumbs-up"></i>',
          confirmButtonAriaLabel: 'Ok!'
        })
      }
    }

    return null
  }
  function deleteLog (uuid) {
    GameService.deleteLog(uuid)
  }
  function deleteSubstitution (uuid) {
    GameService.deleteSubstitution(uuid)
  }
  function deleteGameLog (data) {
    const i = gameLogs.value.map(item => item.uuid).indexOf(data.log.uuid) // find index of your object
    gameLogs.value.splice(i, 1)
    Vue.notify({
      group: 'deletedLogs',
      data: data.log,
      duration: 3000
    })
    model.value.score = data.score
    model.value.periodScores = data.periodScores
    if (data.currentPeriodRoundScore !== undefined) {
      model.value.currentPeriodRoundScore = data.currentPeriodRoundScore
    }
    model.value.homeTeam.players = data.homePlayers
    model.value.awayTeam.players = data.awayPlayers
    model.value.homeTeam.period_stats = data.home_team_period_stats
    model.value.homeTeam.by_period_stats = data.home_team_by_period_stats
    model.value.awayTeam.period_stats = data.away_team_period_stats
    model.value.awayTeam.by_period_stats = data.away_team_by_period_stats
  }
  function stripHtmlTags (str) {
    if ((str === null) || (str === '')) {
      return ''
    } else {
      str = str.toString()
    }
    return str.replace(/<[^>]*>/g, '')
  }
  function deleteLastLog () {
    if (gameLogs.value.length > 0) {
      deleteLog(gameLogs.value[0].uuid)
    }
  }
  function resetLog (related = '') {
    showMobilePlayerActionsPopup.value = false
    showMobileTeamActionsPopup.value = false

    log.value = {
      on: '',
      teammate: '',
      against: ''
    }

    teamOn.value = ''
    stage.value = 'on'
    type.value = ''
    relatedTo.value = related
  }
  function getStateStatType () {
    const action = relatedTo.value === 'team'
      ? teamActions.value[type.value]
      : playerActionsAll.value[type.value]
    return Object.keys(action[stage.value])[0]
  }
  function setPeriod (type, period) {
    GameService.setPeriod({ periodType: type, periodNumber: period })
  }
  function gameUpdatePeriod () {
    GameService.live().then(async (response) => {
      await store.dispatch('main/setModel', response.data)

      setTimeout(() => {
        store.commit('loaded')
      })
    })
  }
  function setData (data) {
    model.value = data
  }
  function endGame () {
    store.commit('main/mobileSettings', false)
    Swal.fire({
      title: VueI18n.t('swal.endgame.title'),
      text: VueI18n.t('swal.endgame.text'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: VueI18n.t('swal.endgame.yes'),
      cancelButtonText: VueI18n.t('swal.endgame.no'),
      showCloseButton: true,
      showLoaderOnConfirm: true
    }).then((result) => {
      if (result.value) {
        store.commit('loading')
        localStorage.removeItem(`timer.time.${model.value.uuid}`)
        GameService.endGame().then(() => {
          router.push({ name: 'archive' })
        })
      }
    })
  }
  async function onDestroy () {
    window.removeEventListener('resize', screenCheck)
    window.removeEventListener('keydown', keyDownListener)
    await unsubscribePublicChannel(`${clientId.value}.record.${model.value.uuid}`)
    // await unsubscribePublicChannel(`${clientId.value}.game.${model.value.uuid}.stopwatch`)
  }

  function screenCheck () {
    if (window.innerWidth >= 600) {
      store.dispatch('main/setIsMobile', false)
    } else {
      store.dispatch('main/setIsMobile', true)
    }
  }

  async function fetchLogs () {
    try {
      const logsResponse = await GameService.logs()
      if (logsResponse && logsResponse.data) {
        gameLogs.value = logsResponse.data
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error)
    }
  }

  async function fetchSubstitutions () {
    try {
      const substitutionsResponse = await GameService.liveSubstitutions()
      if (substitutionsResponse && substitutionsResponse.data) {
        substitutions.value = substitutionsResponse.data
      }
    } catch (error) {
      console.error('Failed to fetch substitutions:', error)
    }
  }

  async function onInit () {
    screenCheck()
    id.value = uuidv4()

    window.addEventListener('resize', screenCheck)
    window.addEventListener('keydown', keyDownListener)

    initPositionAssignments()

    // Fetch logs from the new endpoint
    await fetchLogs()

    // Fetch substitutions
    await fetchSubstitutions()

    initializeLineup()

    const channel = getPublicChannel(`${clientId.value}.record.${model.value.uuid}`)

    await channel.subscribe(async (message) => {
      switch (message.name) {
        case 'substitution_deleted':
          model.value.homeTeam.players = message.data.homePlayers
          model.value.awayTeam.players = message.data.awayPlayers
          initPositionAssignments()
          await fetchSubstitutions()
          break
        case 'substitution':
          model.value.homeTeam.players = message.data.homePlayers
          model.value.awayTeam.players = message.data.awayPlayers
          initPositionAssignments()
          await fetchSubstitutions()
          break
        case 'update':
          if (message.data.add) {
            gameUpdate(message.data.game, message.data.resource)
          } else {
            deleteGameLog(message.data.resource)
          }

          if (message.data.afterGameLogsExists) {
            fetchLogs()
          }
          break

        case 'update.period':
          gameUpdatePeriod()
          break
      }
    })

    const as = localStorage.getItem('autoUnselect')
    if (as === 'true' || as === 'false') {
      store.commit('main/autoUnselect', (as === 'true'))
    } else {
      store.commit('main/autoUnselect', config.value.live.auto_unselect)
    }
  }

  watch(isKeyboardShortcutsEnabled, (newValue) => {
    keyboardShortcuts.value = newValue
  })
  watch(keyboardShortcuts, (newValue) => {
    isKeyboardShortcutsEnabled.value = newValue
  })

  function openStopwatchEditModal () {
    if (!isActiveCurrentStopwatch.value) {
      return
    }

    resetLog()
    keyboardShortcuts.value = false
    prepareTimeInputs()
    Vue.prototype.$modal.show('small-stopwatch-edit-modal')
  }

  function focusSubstitutionModal () {
    Vue.nextTick(() => {
      const modal = document.querySelector('.substitution-modal-overlay')
      if (modal) modal.focus()
    })
  }

  function openSubstitutionModal () {
    editingSubstitutionUuid.value = null
    editingSubstitutionPeriodUuid.value = null
    isSidebarOpen.value = false
    focusedPlayerIndex.value = -1
    focusedTeam.value = 'home'
    showSubstitutionModal.value = true
    keyboardShortcuts.value = false

    selectedSubstitutions.value = []
    previousSubstitutions.value = []

    initPositionAssignments()
    initDraftPositions()

    if (substitutionMode.value === 'on-court') {
      const homeInGame = model.value.homeTeam.players?.filter(p => p.isInGame).map(p => p.uuid) ?? []
      const awayInGame = model.value.awayTeam.players?.filter(p => p.isInGame).map(p => p.uuid) ?? []
      selectedSubstitutions.value = [...homeInGame, ...awayInGame]
    }

    // Initialize substitution time with current game time
    substitutionTime.value = diffTime.value
    substitutionTimeInputs.value = ['', '', '', '']
    substitutionInputErrors.value = [false, false, false, false]

    focusSubstitutionModal()
  }

  async function editSubstitution (uuid) {
    try {
      const { data } = await GameService.showSubstitution(uuid)
      isSidebarOpen.value = false
      editingSubstitutionUuid.value = uuid
      editingSubstitutionPeriodUuid.value = data.substitution.gamePeriodUuid || null
      substitutionTime.value = data.substitution.time
      selectedSubstitutions.value = data.substitution.playersInGame || []
      previousSubstitutions.value = data.previousSubstitution?.playersInGame || []
      substitutionMode.value = 'on-court'

      focusedPlayerIndex.value = -1
      focusedTeam.value = 'home'
      showSubstitutionModal.value = true
      keyboardShortcuts.value = false

      substitutionTimeInputs.value = ['', '', '', '']
      substitutionInputErrors.value = [false, false, false, false]

      initPositionAssignments()
      initDraftPositions()

      focusSubstitutionModal()
    } catch (e) {
      console.error('Failed to fetch substitution details', e)
    }
  }

  const placeholderSubstitutionTime = computed(() => {
    const mins = Math.floor(substitutionTime.value / 1000 / 60).toString().padStart(2, '0')
    const secs = (Math.floor(substitutionTime.value / 1000) % 60).toString().padStart(2, '0')
    return [mins[0], mins[1], secs[0], secs[1]]
  })

  const allPlayersForSubstitution = computed(() => {
    if (substitutionMode.value === 'on-court') {
      const home = model.value.homeTeam.players.slice().sort((a, b) => a.number_of_jersey - b.number_of_jersey)
      const away = model.value.awayTeam.players.slice().sort((a, b) => a.number_of_jersey - b.number_of_jersey)
      return [...home, ...away]
    }
    const homeIn = model.value.homeTeam.players.filter(p => p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    const homeOut = model.value.homeTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    const awayIn = model.value.awayTeam.players.filter(p => p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    const awayOut = model.value.awayTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)

    return [...homeIn, ...homeOut, ...awayIn, ...awayOut]
  })

  const sortInGamePlayers = (players) => {
    return players.slice().sort((a, b) => {
      const posA = inGamePositionOrder(a)
      const posB = inGamePositionOrder(b)
      if (posA !== posB) return posA - posB
      return a.number_of_jersey - b.number_of_jersey
    })
  }

  const homePlayersForSubstitution = computed(() => {
    if (substitutionMode.value === 'on-court') {
      return model.value.homeTeam.players.slice().sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    }
    const inGame = sortInGamePlayers(model.value.homeTeam.players.filter(p => p.isInGame))
    const outGame = model.value.homeTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    return [...inGame, ...outGame]
  })

  const awayPlayersForSubstitution = computed(() => {
    if (substitutionMode.value === 'on-court') {
      return model.value.awayTeam.players.slice().sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    }
    const inGame = sortInGamePlayers(model.value.awayTeam.players.filter(p => p.isInGame))
    const outGame = model.value.awayTeam.players.filter(p => !p.isInGame).sort((a, b) => a.number_of_jersey - b.number_of_jersey)
    return [...inGame, ...outGame]
  })

  function handleSubstitutionKeyDown (e) {
    if (!showSubstitutionModal.value) return

    const homePlayers = homePlayersForSubstitution.value
    const awayPlayers = awayPlayersForSubstitution.value

    const scrollFocusedIntoView = () => {
      Vue.nextTick(() => {
        const focusedEl = document.querySelector('.player-item.focused-player')
        if (focusedEl) {
          focusedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    }

    if (focusedPlayerIndex.value === -1) {
      if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) {
        e.preventDefault()
        focusedPlayerIndex.value = 0
        focusedTeam.value = reversedTeams.value ? 'away' : 'home'
        scrollFocusedIntoView()
        return
      }
    }

    const currentTeamPlayers = focusedTeam.value === 'home' ? homePlayers : awayPlayers

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (currentTeamPlayers.length > 0) {
        focusedPlayerIndex.value = (focusedPlayerIndex.value + 1) % currentTeamPlayers.length
        scrollFocusedIntoView()
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (currentTeamPlayers.length > 0) {
        focusedPlayerIndex.value = (focusedPlayerIndex.value - 1 + currentTeamPlayers.length) % currentTeamPlayers.length
        scrollFocusedIntoView()
      }
    } else if (e.key === 'ArrowRight') {
      if ((!reversedTeams.value && focusedTeam.value === 'home') || (reversedTeams.value && focusedTeam.value === 'away')) {
        e.preventDefault()
        const targetTeamPlayers = !reversedTeams.value ? awayPlayers : homePlayers
        if (targetTeamPlayers.length > 0) {
          focusedTeam.value = !reversedTeams.value ? 'away' : 'home'
          focusedPlayerIndex.value = Math.min(focusedPlayerIndex.value, targetTeamPlayers.length - 1)
          scrollFocusedIntoView()
        }
      }
    } else if (e.key === 'ArrowLeft') {
      if ((!reversedTeams.value && focusedTeam.value === 'away') || (reversedTeams.value && focusedTeam.value === 'home')) {
        e.preventDefault()
        const targetTeamPlayers = !reversedTeams.value ? homePlayers : awayPlayers
        if (targetTeamPlayers.length > 0) {
          focusedTeam.value = !reversedTeams.value ? 'home' : 'away'
          focusedPlayerIndex.value = Math.min(focusedPlayerIndex.value, targetTeamPlayers.length - 1)
          scrollFocusedIntoView()
        }
      }
    } else if (e.key === ' ') {
      e.preventDefault()
      if (focusedPlayerIndex.value !== -1) {
        const player = currentTeamPlayers[focusedPlayerIndex.value]
        if (player) {
          togglePlayerSelection(player)
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      confirmSubstitution()
    } else if (e.key === 'm' || e.key === 'M') {
      e.preventDefault()
      toggleSubstitutionMode()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closeSubstitutionModal()
    }
  }

  function closeSubstitutionModal () {
    showSubstitutionModal.value = false
    substitutionLoading.value = false
    keyboardShortcuts.value = true
  }

  function isValidSubstitutionTimeInput (index, val) {
    if (val === '') return true
    const numVal = parseInt(val)

    if (index === 2 && numVal > 5) {
      return false
    }

    const placeholders = placeholderSubstitutionTime.value
    const currentInputs = [...substitutionTimeInputs.value]

    // Create a temporary array of inputs to see what the total time would be
    const tempInputs = [...currentInputs]
    tempInputs[index] = val

    const mins = parseInt((tempInputs[0] || placeholders[0]) + (tempInputs[1] || placeholders[1])) || 0
    const secs = parseInt((tempInputs[2] || placeholders[2]) + (tempInputs[3] || placeholders[3])) || 0
    const totalMs = (mins * 60 + secs) * 1000

    const minBound = Math.min(periodMinTimeOnPeriod.value, periodMaxTimeOnPeriod.value)
    const maxBound = Math.max(periodMinTimeOnPeriod.value, periodMaxTimeOnPeriod.value)

    // For partial inputs (index 0, 1 or 2), we need to check if ANY completion is possible
    if (index === 0) {
      let possible = false
      for (let i = 0; i <= 9; i++) {
        const testMins = parseInt(val + i.toString())
        for (let j = 0; j <= 5; j++) {
          for (let k = 0; k <= 9; k++) {
            const testSecs = j * 10 + k
            const testTotalMs = (testMins * 60 + testSecs) * 1000
            if (testTotalMs >= minBound && testTotalMs <= maxBound) {
              possible = true
              break
            }
          }
          if (possible) break
        }
        if (possible) break
      }
      return possible
    }

    if (index === 1) {
      let possible = false
      const testMins = parseInt((tempInputs[0] || placeholders[0]) + val)
      for (let j = 0; j <= 5; j++) {
        for (let k = 0; k <= 9; k++) {
          const testSecs = j * 10 + k
          const testTotalMs = (testMins * 60 + testSecs) * 1000
          if (testTotalMs >= minBound && testTotalMs <= maxBound) {
            possible = true
            break
          }
        }
        if (possible) break
      }
      return possible
    }

    if (index === 2) {
      let possible = false
      for (let i = 0; i <= 9; i++) {
        const testSecs = parseInt(val + i.toString())
        const testTotalMs = (mins * 60 + testSecs) * 1000
        if (testTotalMs >= minBound && testTotalMs <= maxBound) {
          possible = true
          break
        }
      }
      return possible
    }

    // For index 3, we have the full minute and full second value
    return totalMs >= minBound && totalMs <= maxBound
  }

  function setSubstitutionInputError (idx) {
    substitutionInputErrors.value[idx] = true
    setTimeout(() => {
      substitutionInputErrors.value[idx] = false
    }, 800)
  }

  function onSubstitutionTimeInput (index, event, componentRefs) {
    const input = event.target
    let val = input.value

    // Remove non-digits
    val = val.replace(/\D/g, '')

    if (val === '') {
      substitutionTimeInputs.value[index] = ''
      updateSubstitutionTime()
      return
    }

    // Take only last digit if multiple entered
    if (val.length > 1) {
      val = val.slice(-1)
    }

    // Validate the digit
    const isValid = isValidSubstitutionTimeInput(index, val)

    if (!isValid) {
      substitutionTimeInputs.value[index] = ''
      setSubstitutionInputError(index)
      return
    }

    substitutionInputErrors.value[index] = false
    substitutionTimeInputs.value[index] = val

    updateSubstitutionTime()

    // Auto-focus next input
    if (index < 3 && componentRefs) {
      const nextInput = componentRefs[`subInput${index + 1}`]
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
      }
    }
  }

  function onSubstitutionTimeKeydown (index, event, componentRefs) {
    if (event.key === 'Backspace') {
      if (substitutionTimeInputs.value[index] === '' && index > 0 && componentRefs) {
        event.preventDefault()
        const prevInput = componentRefs[`subInput${index - 1}`]
        if (prevInput) {
          prevInput.focus()
          prevInput.select()
        }
      }
    } else if (event.key === 'ArrowLeft' && index > 0 && componentRefs) {
      event.preventDefault()
      const prevInput = componentRefs[`subInput${index - 1}`]
      if (prevInput) {
        prevInput.focus()
        prevInput.select()
      }
    } else if (event.key === 'ArrowRight' && index < 3 && componentRefs) {
      event.preventDefault()
      const nextInput = componentRefs[`subInput${index + 1}`]
      if (nextInput) {
        nextInput.focus()
        nextInput.select()
      }
    }
  }

  function updateSubstitutionTime () {
    const placeholders = placeholderSubstitutionTime.value
    const mins = parseInt((substitutionTimeInputs.value[0] || placeholders[0]) + (substitutionTimeInputs.value[1] || placeholders[1])) || 0
    const secs = parseInt((substitutionTimeInputs.value[2] || placeholders[2]) + (substitutionTimeInputs.value[3] || placeholders[3])) || 0
    let totalMs = (mins * 60 + secs) * 1000

    if (totalMs > periodMaxTimeOnPeriod.value) {
      totalMs = periodMaxTimeOnPeriod.value
    } else if (totalMs < periodMinTimeOnPeriod.value) {
      totalMs = periodMinTimeOnPeriod.value
    }

    substitutionTime.value = totalMs
  }

  function initializeLineup (isDelete = false) {
    const homeActiveCount = model.value.homeTeam.players?.filter(p => p.isInGame).length ?? 0
    const awayActiveCount = model.value.awayTeam.players?.filter(p => p.isInGame).length ?? 0

    // Ako nema igrača u igri za oba tima, otvori modal za zamene
    if (!isDelete && homeActiveCount === 0 && awayActiveCount === 0 && model.value.hasSubstitutions) {
      setTimeout(() => {
        openSubstitutionModal()
      }, 500)
    }
  }

  function toggleSubstitutionMode () {
    substitutionMode.value = substitutionMode.value === 'classic' ? 'on-court' : 'classic'
    localStorage.setItem('substitutionMode', substitutionMode.value)

    if (substitutionMode.value === 'on-court') {
      const homeInGame = model.value.homeTeam.players?.filter(p => p.isInGame).map(p => p.uuid) ?? []
      const awayInGame = model.value.awayTeam.players?.filter(p => p.isInGame).map(p => p.uuid) ?? []
      selectedSubstitutions.value = [...homeInGame, ...awayInGame]
    } else {
      selectedSubstitutions.value = []
    }
  }

  function autoFillVacantPositions (teamKey) {
    if (!lineupPositions.value.length) return

    const isHome = teamKey === 'home'
    const teamDraftKey = isHome ? 'homeTeam' : 'awayTeam'
    const teamPlayers = isHome ? (model.value.homeTeam.players ?? []) : (model.value.awayTeam.players ?? [])

    const willBeInGame = getWillBeInGameUuids(teamPlayers)

    const getEffPos = (uuid) => {
      const draft = draftPositionAssignments.value[teamDraftKey]
      if (draft && uuid in draft) return draft[uuid]
      const player = teamPlayers.find(p => p.uuid === uuid)
      return (player?.isInGame ? player.in_game_position : null) ?? null
    }

    // Bench players selected to come in, in selection order, without a position assigned
    const queue = selectedSubstitutions.value
      .map(uuid => teamPlayers.find(p => p.uuid === uuid))
      .filter(p => p && !p.isInGame)
      .filter(p => {
        const draftPos = getDraftPosition(teamKey, p.uuid)
        return draftPos === undefined || draftPos === null
      })

    if (!queue.length) return

    for (const pos of lineupPositions.value) {
      if (!pos.count) continue
      const filled = willBeInGame.filter(uuid => getEffPos(uuid) === pos.id).length
      const needed = pos.count - filled
      for (let i = 0; i < needed && queue.length > 0; i++) {
        toggleDraftPosition(teamKey, queue.shift().uuid, pos.id)
      }
    }
  }

  function togglePlayerSelection (player) {
    const isHome = (model.value.homeTeam.players ?? []).some(p => p.uuid === player.uuid)
    const teamKey = isHome ? 'home' : 'away'
    const index = selectedSubstitutions.value.indexOf(player.uuid)

    if (index > -1) {
      selectedSubstitutions.value.splice(index, 1)
      // Clear draft position for deselected bench players so the slot re-opens
      if (!player.isInGame) {
        const pos = getDraftPosition(teamKey, player.uuid)
        if (pos !== undefined && pos !== null) {
          toggleDraftPosition(teamKey, player.uuid, pos)
        }
      }
    } else {
      selectedSubstitutions.value.push(player.uuid)
      if (substitutionMode.value === 'classic') {
        autoFillVacantPositions(teamKey)
      }
    }
  }

  function selectAllInGame (teamType) {
    const players = teamType === 'home' ? model.value.homeTeam.players : model.value.awayTeam.players
    if (!players) return

    const inGamePlayers = players.filter(p => p.isInGame)
    const allInGameSelected = inGamePlayers.every(p => selectedSubstitutions.value.includes(p.uuid))

    if (allInGameSelected) {
      // Ako su svi vec selektovani, odselektuj ih sve
      inGamePlayers.forEach(player => {
        const index = selectedSubstitutions.value.indexOf(player.uuid)
        if (index > -1) {
          selectedSubstitutions.value.splice(index, 1)
        }
      })
    } else {
      // Ako bar jedan nije selektovan, selektuj sve koji su u igri
      inGamePlayers.forEach(player => {
        if (!selectedSubstitutions.value.includes(player.uuid)) {
          selectedSubstitutions.value.push(player.uuid)
        }
      })
    }
  }

  function toggleOnCourtSelection (teamType) {
    const players = teamType === 'home' ? model.value.homeTeam.players : model.value.awayTeam.players
    if (!players) return

    const playerUuids = players.map(p => p.uuid)
    const hasAnySelected = playerUuids.some(uuid => selectedSubstitutions.value.includes(uuid))

    if (hasAnySelected) {
      // Unselect all for this team
      selectedSubstitutions.value = selectedSubstitutions.value.filter(uuid => !playerUuids.includes(uuid))
    } else {
      // Select currently in-game players
      const inGameUuids = players.filter(p => p.isInGame).map(p => p.uuid)
      selectedSubstitutions.value = [...selectedSubstitutions.value, ...inGameUuids]
    }
  }

  function confirmSubstitution () {
    if (!isSubstitutionValid.value) return

    const applySubstitutions = async () => {
      substitutionLoading.value = true
      const homePlayers = model.value.homeTeam.players || []
      const awayPlayers = model.value.awayTeam.players || []

      const homeTeamPlayersInGame = []
      const awayTeamPlayersInGame = []

      if (substitutionMode.value === 'on-court') {
        homePlayers.forEach(p => {
          if (selectedSubstitutions.value?.includes(p.uuid)) homeTeamPlayersInGame.push(p.uuid)
        })
        awayPlayers.forEach(p => {
          if (selectedSubstitutions.value?.includes(p.uuid)) awayTeamPlayersInGame.push(p.uuid)
        })
      } else {
        homePlayers.forEach(p => {
          const willBePlaying = selectedSubstitutions.value?.includes(p.uuid) ? !p.isInGame : p.isInGame
          if (willBePlaying) homeTeamPlayersInGame.push(p.uuid)
        })
        awayPlayers.forEach(p => {
          const willBePlaying = selectedSubstitutions.value?.includes(p.uuid) ? !p.isInGame : p.isInGame
          if (willBePlaying) awayTeamPlayersInGame.push(p.uuid)
        })
      }

      try {
        const filterNulls = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined))
        const allPositions = {
          ...filterNulls(draftPositionAssignments.value.homeTeam),
          ...filterNulls(draftPositionAssignments.value.awayTeam)
        }

        if (editingSubstitutionUuid.value) {
          await GameService.updateSubstitution(editingSubstitutionUuid.value, {
            gamePeriodUuid: editingSubstitutionPeriodUuid.value || model.value.currentGamePeriodUuid,
            time: substitutionTime.value,
            homeTeamPlayersInGame,
            awayTeamPlayersInGame,
            positions: allPositions
          })
        } else {
          await GameService.substitution({
            gamePeriodUuid: model.value.currentGamePeriodUuid,
            time: substitutionTime.value,
            homeTeamPlayersInGame,
            awayTeamPlayersInGame,
            positions: allPositions
          })
        }

        // Apply draft positions to model immediately so the view reflects the
        // change even when no substitution was created (position-only update).
        const homeDraft = draftPositionAssignments.value.homeTeam
        const awayDraft = draftPositionAssignments.value.awayTeam
        if (model.value.homeTeam?.players) {
          model.value.homeTeam.players = model.value.homeTeam.players.map(p =>
            p.uuid in homeDraft ? { ...p, in_game_position: homeDraft[p.uuid] || null } : p
          )
        }
        if (model.value.awayTeam?.players) {
          model.value.awayTeam.players = model.value.awayTeam.players.map(p =>
            p.uuid in awayDraft ? { ...p, in_game_position: awayDraft[p.uuid] || null } : p
          )
        }
        initPositionAssignments()

        closeSubstitutionModal()
      } catch (e) {
        substitutionLoading.value = false
        console.error('Substitution failed', e)
        Swal.fire({
          icon: 'error',
          title: VueI18n.t('error'),
          text: VueI18n.t('game.substitution_failed') || 'Substitution failed'
        })
      }
    }

    if (substitutionMode.value === 'on-court') {
      const homePlayers = model.value.homeTeam.players || []
      const awayPlayers = model.value.awayTeam.players || []

      const homeSelectedCount = homePlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid)).length
      const awaySelectedCount = awayPlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid)).length

      const teamsWithFewerPlayers = []
      if (homeSelectedCount < maxTeamPlayersInGame.value) {
        teamsWithFewerPlayers.push(model.value.homeTeam.name)
      }
      if (awaySelectedCount < maxTeamPlayersInGame.value) {
        teamsWithFewerPlayers.push(model.value.awayTeam.name)
      }

      if (teamsWithFewerPlayers.length > 0) {
        Swal.fire({
          title: VueI18n.t('are_you_sure'),
          text: VueI18n.t('game.teams_fewer_players_warning', { teams: teamsWithFewerPlayers.join(', '), maxInGameTeamPlayerCount: maxTeamPlayersInGame.value }),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: VueI18n.t('yes_confirm'),
          cancelButtonText: VueI18n.t('cancel')
        }).then((result) => {
          if (result.isConfirmed) {
            applySubstitutions()
          }
        })
      } else {
        applySubstitutions()
      }
      return
    }

    const homePlayers = model.value.homeTeam.players || []
    const awayPlayers = model.value.awayTeam.players || []

    const homeTeamSelected = homePlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid))
    const awayTeamSelected = awayPlayers.filter(p => selectedSubstitutions.value?.includes(p.uuid))

    const validateTeam = (teamSelected, teamName, playersOnTeam) => {
      const currentInGameCount = playersOnTeam.filter(p => p.isInGame).length
      const inGameSelectedCount = teamSelected.filter(p => p.isInGame).length
      const outGameSelectedCount = teamSelected.filter(p => !p.isInGame).length
      const newInGameCount = currentInGameCount - inGameSelectedCount + outGameSelectedCount

      if (newInGameCount > maxTeamPlayersInGame.value) {
        Swal.fire({
          icon: 'warning',
          title: VueI18n.t('game.substitution_limit_reached'),
          text: VueI18n.t('game.substitution_max_5_players', { count: maxTeamPlayersInGame.value }),
          confirmButtonText: 'OK'
        })
        return { valid: false }
      }
      return { valid: true, newInGameCount, teamName }
    }

    const homeValidation = validateTeam(homeTeamSelected, model.value.homeTeam.name, model.value.homeTeam.players)
    if (!homeValidation.valid) return

    const awayValidation = validateTeam(awayTeamSelected, model.value.awayTeam.name, model.value.awayTeam.players)
    if (!awayValidation.valid) return

    const teamsWithFewerPlayers = []
    if (homeValidation.newInGameCount < maxTeamPlayersInGame.value) {
      teamsWithFewerPlayers.push(homeValidation.teamName)
    }
    if (awayValidation.newInGameCount < maxTeamPlayersInGame.value) {
      teamsWithFewerPlayers.push(awayValidation.teamName)
    }

    if (teamsWithFewerPlayers.length > 0) {
      Swal.fire({
        title: VueI18n.t('are_you_sure'),
        text: VueI18n.t('game.teams_fewer_players_warning', { teams: teamsWithFewerPlayers.join(', '), maxInGameTeamPlayerCount: maxTeamPlayersInGame.value }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: VueI18n.t('yes_confirm'),
        cancelButtonText: VueI18n.t('cancel')
      }).then((result) => {
        if (result.isConfirmed) {
          applySubstitutions()
        }
      })
    } else {
      applySubstitutions()
    }
  }

  const isTeamAllInGameSelected = (teamType) => {
    const players = teamType === 'home' ? model.value.homeTeam.players : model.value.awayTeam.players
    if (!players) return false
    const inGamePlayers = players.filter(p => p.isInGame)
    if (inGamePlayers.length === 0) return false
    return inGamePlayers.every(p => selectedSubstitutions.value.includes(p.uuid))
  }

  const isTeamAnySelected = (teamType) => {
    const players = teamType === 'home' ? model.value.homeTeam.players : model.value.awayTeam.players
    if (!players) return false
    return players.some(p => selectedSubstitutions.value.includes(p.uuid))
  }

  function toggleSidebar (tab = null) {
    if (!isSidebarOpen.value) {
      isSidebarOpen.value = true
      sidebarTab.value = tab || 'logs'
      return
    }

    if (tab && tab !== sidebarTab.value) {
      sidebarTab.value = tab
      return
    }

    isSidebarOpen.value = false
  }

  return {
    openSubstitutionModal,
    closeSubstitutionModal,
    focusSubstitutionModal,
    openStopwatchEditModal,
    toggleSubstitutionMode,
    togglePlayerSelection,
    selectAllInGame,
    toggleOnCourtSelection,
    confirmSubstitution,
    isTeamAllInGameSelected,
    isTeamAnySelected,
    onSubstitutionTimeInput,
    onSubstitutionTimeKeydown,
    toggleSidebar,
    fetchLogs,
    onInit,
    onDestroy,
    substitutions,
    gameLogs,
    sidebarTab,
    isSidebarOpen,
    placeholderSubstitutionTime,
    focusedPlayerIndex,
    focusedTeam,
    currentTimeToShow,
    isActiveCurrentStopwatch,
    activeStopwatch,
    stopwatchId,
    handleSubstitutionKeyDown,
    selectedSubstitutions,
    previousSubstitutions,
    substitutionTimeInputs,
    substitutionInputErrors,
    countdownLoaded,
    isMobile,
    clickedButton,
    selectedPlayerHome,
    selectedPlayerAway,
    log,
    timerTime,
    teamOn,
    stage,
    type,
    relatedTo,
    teamSelect,
    playerSelect,
    playerSelection,
    isTouchHold,
    isTimeEditing,
    ablyChannel,
    homeTeamOrderClass,
    actionsOrderClass,
    awayTeamOrderClass,
    clientId,
    actionsPosition,
    invertedTable,
    currentPeriod,
    score,
    scoreByPeriod,
    roundScore,
    playerPreview,
    buttonPlayerGroups,
    buttonTeamGroups,
    buttonPlayerTypes,
    buttonTeamTypes,
    doneDisable,
    homeLabel,
    awayLabel,
    homePlayersSorted,
    homePlayingCount,
    awayPlayersSorted,
    awayPlayingCount,
    maxTeamPlayersInGame,
    isSubstitutionValid,
    substitutionDiff,
    model,
    config,
    reversedTeams,
    disableHome,
    disableAway,

    lineupPositions,
    positionAssignments,
    positionRequirementsValid,
    missingPositions,
    draftMissingPositions,
    initPositionAssignments,
    draftPositionAssignments,
    initDraftPositions,
    getDraftPosition,
    toggleDraftPosition,

    // method
    triggerPlayerActionsPopup,
    showPlayerActionsPopup,
    translateStat,
    playerPositionLabel,
    playerPositionColor,
    translateLog,
    keyDownListener,
    clickTypeButton,
    selectEntity,
    selectType,
    acceptLog,
    storeLog,
    gameUpdate,
    checkForNotifications,
    checkTeamNotifications,
    checkPlayerNotifications,
    deleteLog,
    deleteSubstitution,
    deleteGameLog,
    stripHtmlTags,
    deleteLastLog,
    resetLog,
    getStateStatType,
    currentPeriodType,
    setPeriod,
    gameUpdatePeriod,
    setData,
    endGame,
    gameLogColor,
    timer,
    teamContextMenu,
    playerContextMenu,
    keyboardShortcuts,
    playerActionsScroll,
    playerActions,
    playerPopupBodyMaxHeight,
    showMobileTeamActionsPopup,
    showMobilePlayerActionsPopup,
    showOptionsModal,
    optionsModalStack,
    selectOption,
    closeOptionsModal,
    optionsModalBack,
    showSubstitutionModal,
    editSubstitution,
    editingSubstitutionUuid,
    substitutionLoading,
    substitutionMode,
    allPlayersForSubstitution,
    homePlayersForSubstitution,
    awayPlayersForSubstitution
  }
}
